import { useEffect, useMemo, useRef, useState } from "react";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useChatPolling } from "./useChatPolling";
import { toast } from "sonner";
import { TModelType } from "@/zapi-frontend/shared/types/models";
import { useChatGenerate } from "./useChatGenerate";
import { ChatMessage as ChatMessageType, useChatStore } from "@/zapi-frontend/stores/chatStore";
import { useParams } from "react-router-dom";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import $api from "@/zapi-frontend/shared/api/axios";

import { TChapter } from "@/zapi-frontend/shared/types/projects";

type TUseChatOptions = {
  chapter?: TChapter;
  askOnly?: boolean;
};

export function useChat(options: TUseChatOptions = {}) {
  const { activeChapter } = useChapterStore();
  const { id: projectId } = useParams() as { id: string };
  const chatChapter = options.chapter ?? activeChapter;
  const chapterId = chatChapter?.id.toString() || "";
  const {
    clearChatHistory,
    getLoadingState,
    resetLoadingState,
    getMessagesWithPending,
    addMessage,
    removeLastMessage,
    setLoadingState,
    loadingStates
  } = useChatStore();

  const { startPolling, stopPolling, pollOnce, resetPrereqLoadingStates } = useChatPolling(projectId);
  const { selectedProject } = useProjectStore();
  const hasPolledOnceRef = useRef(false);

  useEffect(() => {
    if (hasPolledOnceRef.current) return;
    hasPolledOnceRef.current = true;
    pollOnce();
  }, [pollOnce]);

  const [isPolling, setIsPolling] = useState(false);
  const activeLoadingStates = useMemo(
    () =>
      Object.entries(loadingStates)
        .filter(([key, value]) => key.startsWith(projectId) && value.pendingGenerationId)
        .map(([key, value]) => ({ ...value, chapterId: key.split(":")[1] })),
    [projectId, loadingStates]
  );

  useEffect(() => {
    if (activeLoadingStates.length > 0) {
      setIsPolling(true);
    } else {
      setIsPolling(false);
    }
  }, [activeLoadingStates]);

  useEffect(() => {
    if (isPolling) {
      startPolling();
    } else {
      stopPolling();
    }
  }, [isPolling, startPolling, stopPolling, selectedProject, projectId]);

  const { startGeneration, isSendingRequest, isAskStreaming, streamingChapterId, abortAskStream } = useChatGenerate();

  const handleSubmit = async (messageText: string, model: TModelType | null) => {
    if (!projectId || !chapterId) return;

    const userMessage: ChatMessageType = {
      role: "user",
      content: messageText,
      id: `user-${Date.now()}`
    };

    addMessage(projectId, chapterId, userMessage);
    setLoadingState(projectId, chapterId, {
      lastUserMessageRef: {
        projectId,
        chapterId,
        messageId: userMessage.id
      }
    });
    await startGeneration(projectId, chapterId, chatChapter?.activeMemo?.mdContent || "", model, options.askOnly);
  };

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [isAbortingRequest, setIsAbortingRequest] = useState(false);
  const handleClearChat = () => {
    if (!projectId || !chapterId) {
      return;
    }
    clearChatHistory(projectId, chapterId);
    setIsConfirmationDialogOpen(false);
  };

  const openConfirmationDialog = () => {
    setIsConfirmationDialogOpen(true);
  };

  const closeConfirmationDialog = () => {
    setIsConfirmationDialogOpen(false);
  };

  const handleAbortRequest = async () => {
    if (!projectId || !chapterId) {
      return;
    }
    setIsAbortingRequest(true);

    if (isAskStreaming) {
      abortAskStream();
      removeLastMessage(projectId, chapterId);
      setIsAbortingRequest(false);
      resetLoadingState(projectId, chapterId);
      return;
    }

    try {
      await $api.post("/chat/cancel", { generationId: pendingGenerationId });
      removeLastMessage(projectId, chapterId);
      resetPrereqLoadingStates(chapterId);
      toast.success("Request aborted", { position: "top-right" });
    } catch (error) {
      toast.error("Failed to abort request", { position: "top-right" });
      console.error("Failed to cancel generation:", error);
    } finally {
      setIsAbortingRequest(false);
    }
    resetLoadingState(projectId, chapterId);
  };

  const messages = getMessagesWithPending(projectId, chapterId);
  const { pendingGenerationId, loadingMessageId, progressMessage, isGeneratingChapter, prereqsChaptersToBeGenerated, allPrereqChapters } =
    getLoadingState(projectId, chapterId || "");
  return {
    projectId,
    handleClearChat,
    isConfirmationDialogOpen,
    openConfirmationDialog,
    closeConfirmationDialog,
    handleAbortRequest,
    messages,
    hasMessages: messages.length > 0,
    isPolling,
    handleSubmit,
    isLoading: !!pendingGenerationId || (isAskStreaming && streamingChapterId === chapterId),
    loadingMessageId,
    progressMessage,
    isGeneratingChapter,
    prereqsChaptersToBeGenerated,
    allPrereqChapters,
    isInitializing: !chapterId,
    isSendingRequest,
    isAbortingRequest,
    isAskStreaming
  };
}
